import { useTranslation } from "@redotech/redo-i18n";
import { folderTypes } from "@redotech/redo-views-common/folders";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import {
  RedoModal,
  RedoModalButtonProps,
} from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { memo, useState } from "react";
import { ConfirmDeleteReturnViewFolderModal } from "./confirm-delete-return-view-folder-modal";

export const EditReturnViewFolderModal = memo(
  function EditReturnViewFolderModal({
    isOpen,
    onClose,
    onSubmit,
    selectedFolder,
    deleteFolder,
    reservedFolderNames,
  }: {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (folderId: string, folderName: string) => Promise<void>;
    selectedFolder: folderTypes["returns"];
    deleteFolder: (folderId: string) => Promise<void>;
    reservedFolderNames: Set<string>;
  }) {
    const { t } = useTranslation();
    const [folderName, setFolderName] = useState(selectedFolder.name);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showConfirmDelete, setShowConfirmDelete] = useState(false);

    const submitDisabled =
      !folderName.trim().length || reservedFolderNames.has(folderName.trim());

    const handleSubmit = async () => {
      try {
        setIsSubmitting(true);
        await onSubmit(selectedFolder._id, folderName);
        onClose();
      } finally {
        setIsSubmitting(false);
        setFolderName("");
      }
    };

    const primaryButton: RedoModalButtonProps = {
      text: t("Save"),
      onClick: handleSubmit,
      disabled: !!submitDisabled,
      pending: false,
      size: "sm",
    };

    const secondaryButton: RedoModalButtonProps = {
      text: t("Delete"),
      onClick: () => setShowConfirmDelete(true),
      disabled: false,
      theme: "destructive",
      size: "sm",
    };

    return (
      <>
        <RedoModal
          isOpen={isOpen}
          modalSize="sm"
          onModalCloseRequested={isSubmitting ? () => {} : onClose}
          primaryButton={primaryButton}
          secondaryButton={secondaryButton}
          title={t("Edit folder")}
        >
          <div data-devinid="folder-name-input">
            <RedoTextInput
              label={t("Folder name")}
              setValue={setFolderName}
              size="sm"
              state={isSubmitting ? "readonly" : "default"}
              value={folderName}
            />
          </div>
        </RedoModal>
        <ConfirmDeleteReturnViewFolderModal
          folderName={selectedFolder.name}
          isOpen={showConfirmDelete && !!selectedFolder}
          onClose={() => setShowConfirmDelete(false)}
          onConfirm={async () => {
            if (selectedFolder) {
              await deleteFolder(selectedFolder._id);
              onClose();
            }
          }}
        />
      </>
    );
  },
);
